import React, {useLayoutEffect} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {MainPageContent, MainPageHeader} from "../app/pages/MainPage";
import {Button} from "../common/Button/Button";
import {applicationFeatures} from "../../utilities/constants";
import NetworkConfigurationModel, {NetworkConfigurationApi} from "../../models/settings/NetworkConfigurationModel";
import {SettingsRowValue} from "../common/CustomTable/CustomTable";
import UpdateNetworkConfigurationForm from "./UpdateNetworkConfigurationForm";
import Text from "../common/Text/Text";
import {isNotEmptyNorFalsy} from "../../utilities/helperFunctions";
import PopupModel from "../../models/scheduler/PopupModel";
import SchedulerModel from "../../models/scheduler/SchedulerModel";
import Collapsable from "../common/Collapsable/Collapsable";

function NetworkConfigurationDisplay(props) {
    const {t} = useTranslation(['automateLicence', 'common']);
    const dispatch = useDispatch();


    const networkConfiguration = useSelector(state => state.networkConfiguration);
    const {isFormActive} = useSelector(state => state.componentStates.networkConfigurationDisplay);
    const {features} = useSelector(state => state.currentUser);

    useLayoutEffect(() => {
        dispatch(NetworkConfigurationModel.componentActionCreators.setDisplayActive());
        return () => dispatch(NetworkConfigurationModel.componentActionCreators.setDisplayInactive());
    }, [dispatch]);


    function showUpdateForm() {
        dispatch(NetworkConfigurationModel.componentActionCreators.updateDisplay({isFormActive: true}));
    }

    function clearConfiguration() {
        dispatch(PopupModel.actionCreators.showWarning({
            info: {
                key: 'proxyServerClear'
            },
            buttons: [{
                titleKey: 'networkConfiguration:option.clear',
                onClick: async () => {
                    try {
                        const {data} = await NetworkConfigurationApi.clear();
                        dispatch(NetworkConfigurationModel.actionCreators.setDetails(data));
                    } catch (error) {
                        dispatch(SchedulerModel.actionCreators.handleResponseError(error));
                    }
                }
            }]
        }));
    }

    const canModify = features.includes(applicationFeatures.MODIFY_AUTOMATE_LICENCE);

    return (
        <MainPageContent {...props}
            title={
                <MainPageHeader text={t('networkConfiguration:page.title')}
                    description={t('networkConfiguration:page.description')}/>
            }
            content={
            <>
                <div className="display-item">
                    <Collapsable label={t('networkConfiguration:label.proxyServer')}>
                        {canModify &&
                            <div className="display-input" style={{display: 'flex', gap: '0.5rem'}}>
                                <Button text={t('networkConfiguration:option.update')}
                                    onClick={showUpdateForm}/>

                                <Button text={t('networkConfiguration:option.clear')}
                                    onClick={clearConfiguration} isDisabled={!networkConfiguration.proxyConfigured}/>
                            </div>
                        }

                        {networkConfiguration.proxyConfigured &&
                            <div className="settings-table display-input">
                                <div className="table-row-group">

                                    <SettingsRowValue label={t('networkConfiguration:label.proxyHost')}
                                        value={networkConfiguration.proxyHost}/>

                                    <SettingsRowValue label={t('networkConfiguration:label.proxyPort')}
                                        value={networkConfiguration.proxyPort}/>

                                    {isNotEmptyNorFalsy(networkConfiguration.nonProxyHosts) &&
                                    <SettingsRowValue label={t('networkConfiguration:label.nonProxyHosts')}
                                        labelStyle={{verticalAlign: 'top'}}
                                        value={networkConfiguration.nonProxyHosts.map((nonHost, index) => (
                                            <Text key={index} ElementTag='span' value={nonHost}/>
                                        ))}/>
                                    }

                                    {isNotEmptyNorFalsy(networkConfiguration.whitelistedCertFingerprints) &&
                                        <SettingsRowValue label={t('networkConfiguration:label.whitelistedFingerprints')}
                                            labelStyle={{verticalAlign: 'top'}}
                                            value={networkConfiguration.whitelistedCertFingerprints.map((fingerprint, index) => (
                                                <Text key={index} ElementTag='span' value={fingerprint}/>
                                            ))}/>
                                    }
                                </div>
                            </div>
                        }
                    </Collapsable>
                </div>
            </>
            }
            children={
                <>
                    {isFormActive &&
                        <UpdateNetworkConfigurationForm/>
                    }
                </>
            }
        />
    )
}

export default NetworkConfigurationDisplay;
